import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Card, H2, Input, Label, Paragraph, TextArea, YStack } from 'tamagui';
import { api } from '../lib/api';
import { friendlyErrorMessage } from '../lib/errors';

const MAX_SUBJECT = 150;
const MAX_BODY = 5000;

/**
 * Signed-in help / contact form. Submits to POST /api/contact, which stores the
 * message and emails the site admins. Surfaces idle / sending / sent / error
 * states; on success the form resets so a user can send another note.
 */
export function Help() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.sendContactMessage({
        subject: subject.trim() || undefined,
        body: body.trim(),
      }),
    onSuccess: () => {
      setSubject('');
      setBody('');
    },
  });

  const canSend = body.trim().length > 0 && !mutation.isPending;

  return (
    <YStack gap="$4" maxWidth={680} width="100%" alignSelf="center">
      <H2>Help &amp; contact</H2>

      <Card padding="$4" elevate>
        <YStack gap="$3">
          <Paragraph size="$2" color="$color10">
            Run into a problem or have a question? Send us a message and the
            CarTrack Pro team will get back to you by email.
          </Paragraph>

          <YStack gap="$2">
            <Label htmlFor="contact-subject">Subject (optional)</Label>
            <Input
              id="contact-subject"
              value={subject}
              onChangeText={setSubject}
              placeholder="Short summary"
              maxLength={MAX_SUBJECT}
              disabled={mutation.isPending}
            />
          </YStack>

          <YStack gap="$2">
            <Label htmlFor="contact-body">Message</Label>
            <TextArea
              id="contact-body"
              value={body}
              onChangeText={setBody}
              placeholder="Tell us what's going on…"
              minHeight={160}
              maxLength={MAX_BODY}
              disabled={mutation.isPending}
            />
            <Paragraph size="$1" color="$color10" textAlign="right">
              {body.length} / {MAX_BODY}
            </Paragraph>
          </YStack>

          {mutation.isSuccess ? (
            <Paragraph color="$green10">
              Thanks — your message has been sent. We'll be in touch.
            </Paragraph>
          ) : null}
          {mutation.isError ? (
            <Paragraph color="$red10">
              {friendlyErrorMessage(mutation.error)}
            </Paragraph>
          ) : null}

          <YStack>
            <Button
              theme={canSend ? 'blue' : undefined}
              disabled={!canSend}
              onPress={() => mutation.mutate()}
            >
              {mutation.isPending ? 'Sending…' : 'Send message'}
            </Button>
          </YStack>
        </YStack>
      </Card>
    </YStack>
  );
}
